import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Download, X, Smartphone, Monitor, CheckCircle,
  Plus, MoreVertical, Apple, Play, Bell
} from 'lucide-react';
import { isPWAInstalled, promptInstall, getInstallInstructions, isIOS } from '@/lib/pwa';
import { isAuthenticated, getStoredUser } from '@/lib/auth';
import { ENABLE_MOBILE_APP_PROMO } from '@/lib/featureFlags';

// Routes on which the PWA install popup must NEVER render, regardless of
// prior trigger state (e.g., legal pages linked from external channels).
const SUPPRESSED_ROUTES = ['/terms', '/privacy'];

// Native mobile app store links for Sub (substitute teacher) users.
// TODO(KAN-105): Replace these placeholders with the real published store URLs.
const APP_STORE_URL = '#';
const PLAY_STORE_URL = '#';

export function PWAInstallBanner({ onDismiss }) {
  const location = useLocation();
  const isSuppressedRoute = SUPPRESSED_ROUTES.includes(location.pathname);

  // Auth + role gating. The banner must ONLY be evaluated post-authentication.
  // Recomputed on every render (route changes re-render via useLocation), so it
  // correctly begins evaluating right after a user logs in.
  const authed = isAuthenticated();
  const user = getStoredUser();
  const role = user?.role;
  const isEmployer = role === 'employer'; // "School Admin" -> Web App install prompt
  const isSub = role === 'toc';           // "Sub" -> native mobile app promo
  const isEligibleRole = isEmployer || isSub; // Super Admin (subscription_manager) never sees it

  const [showBanner, setShowBanner] = useState(false);
  const [canPrompt, setCanPrompt] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [installed, setInstalled] = useState(() => isPWAInstalled());
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('pwa_banner_dismissed_session') === 'true'
  );
  const instructions = getInstallInstructions();

  // Register install-related listeners once (only meaningful for the School Admin
  // Web App install flow, but harmless to keep mounted).
  useEffect(() => {
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (e) => {
      if (e.matches) {
        setInstalled(true);
        setShowBanner(false);
      }
    };
    mediaQuery.addEventListener('change', handleDisplayModeChange);

    const handleInstallable = () => setCanPrompt(true);
    const handleInstalled = () => {
      setInstalled(true);
      setShowBanner(false);
      localStorage.setItem('pwa_installed', 'true');
    };

    window.addEventListener('pwa-installable', handleInstallable);
    window.addEventListener('pwa-installed', handleInstalled);

    return () => {
      mediaQuery.removeEventListener('change', handleDisplayModeChange);
      window.removeEventListener('pwa-installable', handleInstallable);
      window.removeEventListener('pwa-installed', handleInstalled);
    };
  }, []);

  // Decide whether to show the banner. Re-evaluates on auth/role/route changes.
  useEffect(() => {
    // Never show pre-login, for ineligible roles, once dismissed this session,
    // or on suppressed routes.
    if (!authed || !isEligibleRole || dismissed || isSuppressedRoute) {
      setShowBanner(false);
      return;
    }

    // School Admin: only relevant if the Web App is not already installed.
    if (isEmployer && isPWAInstalled()) {
      setInstalled(true);
      setShowBanner(false);
      return;
    }

    // Show after a short delay for a smoother post-login experience.
    const timer = setTimeout(() => {
      // Subs get a native-app promo, but ONLY when the mobile app promo feature
      // flag is enabled (kept off until the native app is published).
      // School Admins get the Web App install prompt only if it isn't installed.
      const subShouldShow = isSub && ENABLE_MOBILE_APP_PROMO;
      const employerShouldShow = isEmployer && !isPWAInstalled();
      if (subShouldShow || employerShouldShow) {
        setShowBanner(true);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [authed, role, dismissed, isSuppressedRoute, location.pathname, isEligibleRole, isEmployer, isSub]);

  const handleInstall = async () => {
    if (canPrompt) {
      const result = await promptInstall();
      if (result.outcome === 'accepted') {
        setShowBanner(false);
      }
    } else {
      // Show manual instructions (especially for iOS)
      setShowInstructions(true);
    }
  };

  const handleDismiss = () => {
    // Dismissal persists for the remainder of the session across all routes.
    sessionStorage.setItem('pwa_banner_dismissed_session', 'true');
    setDismissed(true);
    setShowBanner(false);
    onDismiss?.();
  };

  if (!showBanner || isSuppressedRoute || !authed || !isEligibleRole) return null;

  // ============================================================
  // SUB (Substitute Teacher) -> Native mobile app promotion
  // No Web App install button; CTA links to the App Store / Play Store.
  // Gated behind the ENABLE_MOBILE_APP_PROMO feature flag (default: off)
  // until the native mobile app is published. Styling/markup preserved so it
  // can be re-enabled instantly by flipping the flag.
  // ============================================================
  if (isSub) {
    if (!ENABLE_MOBILE_APP_PROMO) return null;
    return (
      <Card
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 shadow-lg border-emerald-200 bg-gradient-to-r from-emerald-50 to-white"
        data-testid="mobile-app-promo-banner"
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
              <Bell className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">Get real-time shift alerts</h3>
                  <p className="text-sm text-slate-600 mt-0.5">
                    Download the ExpressSub mobile app to get instant notifications the moment a new position is posted.
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 -mt-1 -mr-2"
                  onClick={handleDismiss}
                  data-testid="mobile-app-promo-dismiss"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch gap-2 mt-3">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                  data-testid="app-store-link"
                >
                  <Button size="sm" className="w-full bg-slate-900 hover:bg-slate-800">
                    <Apple className="h-4 w-4 mr-1.5" />
                    App Store
                  </Button>
                </a>
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                  data-testid="play-store-link"
                >
                  <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700">
                    <Play className="h-4 w-4 mr-1.5" />
                    Google Play
                  </Button>
                </a>
              </div>
              <button
                type="button"
                className="text-xs text-slate-500 mt-2 hover:text-slate-700"
                onClick={handleDismiss}
              >
                Not now
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ============================================================
  // SCHOOL ADMIN (Employer) -> existing Web App install flow
  //
  // ----------------------------------------------------------------
  // TODO(prod-temp): The bottom-right "Install ExpressSub" PWA
  // install banner for School Admins is TEMPORARILY disabled while
  // the mobile app is being prepared. The full install UI (both the
  // main banner and the iOS/desktop instructions card) is left in
  // place below but short-circuited to `null`. When the mobile app
  // is ready, restore the original `if (installed) return null;` +
  // the `if (showInstructions) { ... }` and the main banner return.
  // ----------------------------------------------------------------
  if (installed) return null;
  return null;

  /* === BEGIN TEMPORARILY DISABLED — re-enable when mobile app is ready ===

  if (showInstructions) {
    return (
      <Card className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 shadow-lg border-emerald-200 bg-white" data-testid="pwa-install-instructions">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              {isIOS() ? <Smartphone className="h-5 w-5 text-emerald-600" /> : <Monitor className="h-5 w-5 text-emerald-600" />}
              <h3 className="font-semibold text-slate-900">Install ExpressSub</h3>
            </div>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowInstructions(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <p className="text-sm text-slate-600 mb-3">
            Install ExpressSub for the best experience with instant notifications:
          </p>

          <ol className="space-y-2 text-sm">
            {instructions.steps.map((step, index) => (
              <li key={step} className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-xs flex items-center justify-center font-medium">
                  {index + 1}
                </span>
                <span className="text-slate-700">{step}</span>
              </li>
            ))}
          </ol>

          {isIOS() && (
            <div className="mt-3 p-2 bg-slate-50 rounded flex items-center gap-2 text-xs text-slate-600">
              <MoreVertical className="h-4 w-4" />
              <span>Look for the more options icon: </span>
              <span className="font-mono bg-white px-1 rounded border">⋯</span>
              <span>then share: </span>
              <span className="font-mono bg-white px-1 rounded border">⬆️</span>
            </div>
          )}

          <Button variant="outline" size="sm" className="w-full mt-3" onClick={handleDismiss}>
            {"Got it, I'll do this later"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 shadow-lg border-emerald-200 bg-gradient-to-r from-emerald-50 to-white" data-testid="pwa-install-banner">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
            <Download className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Install ExpressSub</h3>
                <p className="text-sm text-slate-600 mt-0.5">
                  Get quick access web app on your desktop
                </p>
              </div>
              <Button variant="ghost" size="icon" className="h-6 w-6 -mt-1 -mr-2" onClick={handleDismiss}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleInstall} data-testid="pwa-install-btn">
                <Plus className="h-4 w-4 mr-1" />
                Install App
              </Button>
              <Button variant="ghost" size="sm" onClick={handleDismiss}>
                Not now
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  === END TEMPORARILY DISABLED === */
}

export function PWAInstalledBadge() {
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    setIsInstalled(isPWAInstalled());
  }, []);

  if (!isInstalled) return null;

  return (
    <div className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
      <CheckCircle className="h-3 w-3" />
      <span>App Installed</span>
    </div>
  );
}
